import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, GraduationCap, Gavel, MessageSquare, Scale } from 'lucide-react';
import { AppPage, AppPageHeader } from '@/components/layout/AppPage';
import { buildStarterLaunchPath, buildStarterWorkflows } from '@/lib/activation';

const STUDENT_PANELS = [
  {
    title: 'Explain mode',
    description: 'Turn a judgment, doctrine, or section into clean simple language before going deeper.',
    mode: 'EXPLAIN',
    icon: MessageSquare,
  },
  {
    title: 'Case brief and IRAC',
    description: 'Move from authority to structured case brief, issue framing, rule, analysis, and conclusion.',
    mode: 'EXAM_PREP',
    icon: BookOpen,
  },
  {
    title: 'Moot and oral prep',
    description: 'Use the same trust architecture to prepare likely questions, submissions, and fallback answers.',
    mode: 'STRATEGY',
    icon: Gavel,
  },
  {
    title: 'Authority-led learning',
    description: 'Study the hierarchy of sources, what is binding, what is persuasive, and how to verify legal propositions.',
    mode: 'RESEARCH',
    icon: Scale,
  },
];

export default function StudentModePage() {
  const navigate = useNavigate();
  const workflows = useMemo(() => buildStarterWorkflows('STUDENT'), []);

  return (
    <main className="app-main" data-testid="student-mode-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Student Mode"
          title="Authority-led legal learning"
          description="Explain mode, case briefs, IRAC, exam prep, and moot/oral prep on top of the same trust architecture used for professional work."
          meta={(
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span className="app-page-chip">Professional and learning modes stay clearly separated</span>
              <span className="app-page-chip">Verification still matters here too</span>
            </div>
          )}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
          {STUDENT_PANELS.map((panel) => {
            const workflow = workflows.find((item) => item.mode === panel.mode) || workflows[0];
            const Icon = panel.icon;
            return (
              <button
                key={panel.title}
                type="button"
                onClick={() => navigate(buildStarterLaunchPath(workflow, panel.title))}
                className="advoq-card"
                style={{
                  textAlign: 'left',
                  padding: '22px',
                  borderRadius: '24px',
                  border: '1px solid var(--border-subtle)',
                  background: 'linear-gradient(180deg, color-mix(in srgb, var(--bg-elevated) 88%, var(--accent-primary) 3%), var(--bg-surface))',
                  display: 'grid',
                  gap: '12px',
                }}
              >
                <span style={{ width: '42px', height: '42px', borderRadius: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-elevated)', color: 'var(--accent-primary)' }}>
                  <Icon size={18} />
                </span>
                <div style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: 700 }}>{panel.title}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>{panel.description}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--accent-primary)', fontSize: '13px', fontWeight: 700 }}>
                  Open student workflow <ArrowRight size={14} />
                </div>
              </button>
            );
          })}
        </div>

        <div className="advoq-card" style={{ padding: '20px', borderRadius: '24px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'grid', gap: '10px', marginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-primary)' }}>
            <GraduationCap size={18} />
            <span style={{ fontSize: '16px', fontWeight: 700 }}>Student mode guardrails</span>
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>
            Learning outputs should stay clearly labeled as educational when they are not intended for live professional use. Advoq still keeps trust labels visible so students learn how Indian legal authority actually works.
          </div>
        </div>
      </AppPage>
    </main>
  );
}
